import type { ReactNode } from 'react';
import type { NostrEvent } from '@nostrify/nostrify';
import { MessageSquare } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { CommentsSection } from '@/components/comments/CommentsSection';
import { useComments } from '@/hooks/useComments';

interface CommentsDialogProps {
  /** The wiki article, publication index, or publication section being discussed. */
  event: NostrEvent;
  /** Display title shown in the dialog header (e.g. `articleTitle(event)`). */
  title: string;
  children?: ReactNode;
}

/**
 * "Comments" button + modal for a wiki article or publication (index or
 * section), backed by NIP-22 (kind 1111) comments scoped to this specific
 * addressable event/version — see `@/hooks/useComments` for how the root
 * scope (`A`/`E`/`I` tags) is derived. Threaded replies, posting, and
 * deletion are handled by `CommentsSection` (installed via the
 * `nostr-comments` skill).
 *
 * The trigger button shows a live comment count, matching the visual
 * language of `StarButton`/`ZapButton` (count badge next to the icon)
 * even though comments aren't a toggleable reaction.
 */
export function CommentsDialog({ event, title, children }: CommentsDialogProps) {
  const { data } = useComments(event);
  const count = data?.allComments.length ?? 0;

  return (
    <Dialog>
      <DialogTrigger asChild>
        {children ?? (
          <Button type="button" variant="outline" size="sm" className="gap-1.5" title="Comments">
            <MessageSquare className="size-4" />
            {count > 0 ? count : 'Comments'}
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-h-[85dvh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Comments on "{title}"</DialogTitle>
          <DialogDescription>
            Public, signed Nostr comments (NIP-22) attached to this specific version.
          </DialogDescription>
        </DialogHeader>

        <CommentsSection root={event} />
      </DialogContent>
    </Dialog>
  );
}
